import type { ReactNode } from 'react';
import React, { createContext, useContext } from 'react';
import type { DiscountPresentationMode } from '@noths/polaris-client-site-chrome-ui';

/**
 * Whether the site is presenting discounts as a sale or as an offer. Set site-wide
 * in Contentstack by the E-commerce team so a trading period can be presented either
 * way without a release, and delivered here on the site chrome content every page
 * already fetches.
 *
 * Held in context rather than in Redux because it arrives at `_app` level, outside
 * any page's `getServerSideProps`, and every consumer is a component or a hook.
 *
 * The type is imported from the site chrome package, which parses the Contentstack
 * field. The context itself is local: importing a hook from that package's entry
 * point would pull the whole header bundle into every card call site.
 */
const DiscountPresentationModeContext = createContext<DiscountPresentationMode>('offer');

export const DiscountPresentationModeProvider = ({
  children,
  mode = 'offer',
}: {
  children: ReactNode;
  /** Undefined when the site chrome fetch failed or the field is unset. */
  mode?: DiscountPresentationMode;
}) => (
  <DiscountPresentationModeContext.Provider value={mode}>
    {children}
  </DiscountPresentationModeContext.Provider>
);

/**
 * Defaults to 'offer' outside a provider rather than throwing. Every listing page
 * and carousel reads this, so a missing provider should fall back to the understated
 * treatment rather than take the page down.
 */
export const useDiscountPresentationMode = () => useContext(DiscountPresentationModeContext);

/** The card prop the mode drives, so call sites do not each repeat the comparison. */
export const usePromoteAsSale = () => useDiscountPresentationMode() === 'sale';
